import { useState, useMemo, useEffect } from "react";
import { useInvoices } from "../contexts/InvoicesContext";
import { useExpenses } from "../contexts/ExpensesContext";
import {
  loadProjects,
  calcInvoiceTotal,
  getDateRange,
  buildTrendData,
  groupByProject,
  groupByCategory,
  buildAnnualSummary,
} from "../services/financialReportService";

export function useFinancials() {
  // ── Date range state ──────────────────────────────────────────────────────
  const [preset, setPreset] = useState("this-month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const { start, end } = useMemo(() => {
    if (preset === "custom") {
      const s = customStart || new Date().toISOString().slice(0, 10);
      const e = customEnd || new Date().toISOString().slice(0, 10);
      return { start: s, end: e };
    }
    return getDateRange(preset) || { start: "", end: "" };
  }, [preset, customStart, customEnd]);

  // ── Raw data from contexts ────────────────────────────────────────────────
  const { invoices: allInvoices } = useInvoices();
  const { expenses: allExpenses } = useExpenses();
  const [allProjects, setAllProjects] = useState([]);

  useEffect(() => {
    let alive = true;
    loadProjects()
      .then((projects) => {
        if (alive) setAllProjects(Array.isArray(projects) ? projects : []);
      })
      .catch(() => {
        if (alive) setAllProjects([]);
      });

    return () => {
      alive = false;
    };
  }, []);

  // ── Period-filtered data ──────────────────────────────────────────────────
  const invoices = useMemo(
    () => allInvoices.filter(i => i.issueDate >= start && i.issueDate <= end),
    [allInvoices, start, end],
  );

  const expenses = useMemo(
    () => allExpenses.filter(e => e.date >= start && e.date <= end),
    [allExpenses, start, end],
  );

  // ── Key metrics ───────────────────────────────────────────────────────────
  const metrics = useMemo(() => {
    const sum = list => list.reduce((s, i) => s + calcInvoiceTotal(i.lineItems), 0);

    const totalInvoiced = sum(invoices);
    const totalReceived = sum(invoices.filter(i => i.status === "paid"));
    const totalPending = sum(invoices.filter(i => i.status === "pending"));
    const totalOverdue = sum(invoices.filter(i => i.status === "overdue"));
    const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount), 0);
    const netProfit = totalReceived - totalExpenses;

    return {
      totalInvoiced,
      totalReceived,
      totalPending,
      totalOverdue,
      totalExpenses,
      netProfit,
      invoiceCount: invoices.length,
      paidCount: invoices.filter(i => i.status === "paid").length,
      pendingCount: invoices.filter(i => i.status === "pending").length,
      overdueCount: invoices.filter(i => i.status === "overdue").length,
      expenseCount: expenses.length,
    };
  }, [invoices, expenses]);

  // ── Revenue trend (line chart series) ────────────────────────────────────
  const trendData = useMemo(
    () => (start && end ? buildTrendData(invoices, expenses, start, end) : []),
    [invoices, expenses, start, end],
  );

  // ── Income breakdown by project (top 8 + "Other") ─────────────────────────
  const byProject = useMemo(() => {
    const groups = groupByProject(invoices, allProjects, expenses);
    if (groups.length <= 8) return groups;
    const top = groups.slice(0, 8);
    const other = groups.slice(8).reduce(
      (acc, g) => ({
        ...acc,
        invoiced: acc.invoiced + g.invoiced,
        received: acc.received + g.received,
        expenses: acc.expenses + g.expenses,
        net: acc.net + g.net,
      }),
      { id: null, name: "Other", invoiced: 0, received: 0, expenses: 0, net: 0 },
    );
    return [...top, other];
  }, [invoices, expenses, allProjects]);

  // ── Expense breakdown by category (doughnut) ──────────────────────────────
  const byCategory = useMemo(() => groupByCategory(expenses), [expenses]);

  // ── Annual summary (always full UK tax year — ignores date range) ─────────
  const annualSummary = useMemo(
    () => buildAnnualSummary(allInvoices, allExpenses),
    [allInvoices, allExpenses],
  );

  // ── Outstanding actions (not period-filtered — use ALL invoices/expenses) ─
  const today = new Date().toISOString().slice(0, 10);
  const in14Days = new Date(Date.now() + 14 * 86_400_000).toISOString().slice(0, 10);

  const overdueInvoices = useMemo(
    () => allInvoices
      .filter(i => i.status === "overdue")
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
    [allInvoices],
  );

  const upcomingInvoices = useMemo(
    () => allInvoices
      .filter(i => i.status !== "paid" && i.dueDate >= today && i.dueDate <= in14Days)
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
    [allInvoices, today, in14Days], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const unbilledExpenses = useMemo(
    () => allExpenses.filter(e => e.is_billable && !e.is_billed),
    [allExpenses],
  );

  return {
    // date range
    preset, setPreset,
    customStart, setCustomStart,
    customEnd, setCustomEnd,
    start, end,
    // data
    metrics,
    trendData,
    byProject,
    byCategory,
    annualSummary,
    overdueInvoices,
    upcomingInvoices,
    unbilledExpenses,
    allProjects,
    // raw for export
    invoices,
    expenses,
  };
}
